/**
 * Whether the viewport is at a Tailwind breakpoint or wider: the width rules
 * for layouts that change in JS, not CSS. `useIsDesktop` is `lg`, where
 * AppShell has a collapsed rail (SA-958); `useIsWide` is `xl`, where the
 * Conversations page sets its list beside the chat (SA-961).
 *
 * In rem, never px — a media query's rem is the browser's default font size,
 * so a reader who raised it gets the narrower layout above 1024px (or
 * 1280px) and must get the labeled drawer with it.
 */
import { useSyncExternalStore } from 'react'

/** Tailwind's `lg` (`--breakpoint-lg: 64rem`). */
export const DESKTOP_QUERY = '(min-width: 64rem)'
/** Tailwind's `xl` (`--breakpoint-xl: 80rem`). */
export const WIDE_QUERY = '(min-width: 80rem)'

/** One stable subscribe and snapshot per query, as useSyncExternalStore needs. */
function minWidth(query: string) {
  return {
    subscribe(onChange: () => void) {
      if (typeof window.matchMedia !== 'function') return () => {}
      const mql = window.matchMedia(query)
      mql.addEventListener('change', onChange)
      return () => mql.removeEventListener('change', onChange)
    },
    now() {
      return typeof window.matchMedia === 'function' ? window.matchMedia(query).matches : true
    },
  }
}

const desktop = minWidth(DESKTOP_QUERY)
const wide = minWidth(WIDE_QUERY)

export function useIsDesktop(): boolean {
  return useSyncExternalStore(desktop.subscribe, desktop.now, () => true)
}

export function useIsWide(): boolean {
  return useSyncExternalStore(wide.subscribe, wide.now, () => true)
}
